import {
  alpha,
  Button,
  Card,
  Center,
  PinInput,
  Stack,
  Text,
  Title,
  useComputedColorScheme,
} from '@mantine/core';
import { IconShieldLock } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  type BrowserLoginSuccess,
  type Session,
  sessionExpiriesFromLoginSuccess,
  type Verify2FALoginRequest,
} from '@/api';
import { useLanguageAwareRouting } from '../hooks/useLanguageAwareRouting';

export interface Login2FAPageProps {
  setSession: (session: Session) => void;
  verify2FA: (request: Verify2FALoginRequest) => Promise<BrowserLoginSuccess>;
  onLoginSuccess?: () => void;
  titleGradient?: { from: string; to: string };
  titleClassName?: string;
}

export function Login2FAPage(props: Login2FAPageProps) {
  const { t } = useTranslation();
  const isDark = useComputedColorScheme('light') === 'dark';
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();

  const {
    setSession,
    verify2FA,
    onLoginSuccess,
    titleGradient = { from: 'pink', to: 'yellow' },
    titleClassName,
  } = props;

  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [loginMethod, setLoginMethod] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const [hasError, setHasError] = useState<string | null>(null);

  useEffect(() => {
    const url = new URL(window.location.href);
    const hashParams = new URLSearchParams(url.hash.split('?')[1]);

    const token = hashParams.get('pending_token');
    const method = hashParams.get('login_method');

    if (!token) {
      setHasError(t('login.invalid2FASession'));
      return;
    }

    setPendingToken(token);
    setLoginMethod(method);
  }, [t]);

  const handleVerify = async () => {
    if (!pendingToken || code.length < 6) return;

    setIsBusy(true);
    setHasError(null);

    try {
      const result = await verify2FA({
        pending_token: pendingToken,
        code: code,
      });

      // Set session with tokens. The auth library has already exchanged the
      // auth-code internally and returned a
      // `BrowserLoginSuccess`. The opaque session token lives in the
      // HttpOnly cookie set by the /login/exchange response.
      setSession({
        isLoggedIn: true,
        userId: result.user_id,
        accessToken: result.access_token,
        email: result.email,
        displayName: result.display_name,
        ...sessionExpiriesFromLoginSuccess(result),
        method: loginMethod,
      } as Session);

      if (onLoginSuccess) {
        onLoginSuccess();
      } else {
        nav(createLanguageURL('/account'));
      }
    } catch (e: any) {
      console.error(e);
      const errorData = e?.response?.data;
      if (errorData && typeof errorData === 'object' && errorData.message) {
        setHasError(errorData.message);
      } else if (e?.response?.status === 401) {
        setHasError(t('login.invalid2FACode'));
      } else if (e?.response?.status === 429) {
        setHasError(t('login.tooMany2FAAttempts'));
        nav(createLanguageURL('/login'));
      } else {
        setHasError(`Error: ${e?.message || e}`);
      }
      setCode('');
    } finally {
      setIsBusy(false);
    }
  };

  const handleBack = () => {
    nav(createLanguageURL('/login'));
  };

  return (
    <Center h="100vh">
      <Stack>
        <Title order={1} className={titleClassName} ta="center">
          <Text inherit variant="gradient" component="span" gradient={titleGradient}>
            {t('login.twoFactorAuth')}
          </Text>
        </Title>
        <Card
          maw={400}
          p="xl"
          bg={
            isDark
              ? alpha('var(--mantine-color-gray-8)', 0.6)
              : alpha('var(--mantine-color-gray-0)', 0.6)
          }
        >
          {pendingToken ? (
            <Stack gap="md" align="center">
              <IconShieldLock size={48} stroke={1.5} />
              <Text size="lg" fw={500}>
                {t('login.twoFactorRequired')}
              </Text>
              <Text size="sm" c="dimmed" ta="center">
                {t('login.enterTwoFactorCode')}
              </Text>
              <PinInput
                length={6}
                type="number"
                value={code}
                onChange={setCode}
                oneTimeCode
                disabled={isBusy}
                size="lg"
              />
              {hasError && (
                <Text c="red" size="sm" ta="center">
                  {hasError}
                </Text>
              )}
              <Button onClick={handleVerify} loading={isBusy} disabled={code.length < 6} fullWidth>
                {t('login.verify')}
              </Button>
              <Text size="sm" c="dimmed" ta="center">
                {t('login.useRecoveryCode')}
              </Text>
              <Button variant="subtle" size="xs" onClick={handleBack}>
                {t('login.back')}
              </Button>
            </Stack>
          ) : (
            <Stack gap="md" align="center">
              {hasError && (
                <Text c="red" size="sm" ta="center">
                  {hasError}
                </Text>
              )}
              <Button onClick={handleBack}>{t('login.backToLogin')}</Button>
            </Stack>
          )}
        </Card>
      </Stack>
    </Center>
  );
}
